import {chainCommands, exitCode, joinDown, joinUp, lift, setBlockType, toggleMark, wrapIn} from 'prosemirror-commands';
import {liftListItem, sinkListItem, splitListItem} from 'prosemirror-schema-list';
import {redo, undo} from 'prosemirror-history';
import {NodeSelection, Selection} from "prosemirror-state";
import {keymap} from "prosemirror-keymap";
import {toggleList} from "./plugins/commands";

const mac = typeof navigator != 'undefined' ? /Mac/.test(navigator.platform)
    : false

export const buildKeymaps = (state, nodeKeyMaps, markKeyMaps) => {
    const keys = {};

    const bind = (key, cmd) => {
        keys[key] = cmd;
    };

    const selectParentNode = (state, dispatch) => {
        const ref = state.selection;
        const $from = ref.$from;
        const to = ref.to;
        const same = $from.sharedDepth(to);
        if (same === 0) {
            return false;
        }
        const pos = $from.before(same);
        const selection = NodeSelection.create(state.doc, pos);
        if (dispatch && NodeSelection.isSelectable(selection.node)) {
            dispatch(state.tr.setSelection(selection));
        }
        return true
    };

    bind("Mod-z", undo);
    bind("Shift-Mod-z", redo);

    if (!mac) {
        bind("Mod-y", redo);
    }

    const arrowHandler = (state, dispatch, view, dir) => {
        const selection = state.selection;
        if (selection.empty) {
            const side = dir === "left" || dir === "up" ? -1 : 1;
            if (view.endOfTextblock(dir)) {
                const $head = selection.$head;
                const nextPos = Selection.near(
                    state.doc.resolve(side > 0 ? $head.after() : $head.before()), side);
                if (nextPos.$head && [state.schema.nodes.math_block,
                    state.schema.nodes.code_container_mermaid,
                    state.schema.nodes.code_container_plantuml].includes(
                    nextPos.$head.parent.type)) {
                    const $pos = state.doc.resolve(nextPos.$head.before());
                    dispatch(state.tr.setSelection(new NodeSelection($pos)));
                    return true;
                } else if (nextPos.$head && nextPos.$head.parent.type
                    === state.schema.nodes.code_block) {
                    dispatch(state.tr.setSelection(nextPos));
                    return true;
                }
            } else {
                const nextPos = Selection.near(state.doc.resolve(selection.head + side),
                    side);
                if (nextPos.$head && nextPos.$head.parent.type
                    === state.schema.nodes.math_inline) {
                    const $pos = state.doc.resolve(nextPos.$head.before());
                    dispatch(state.tr.setSelection(new NodeSelection($pos)));
                    return true;
                }
            }
        }

        return false
    }

    bind("ArrowLeft",
        (state, dispatch, view) => arrowHandler(state, dispatch, view, "left"));
    bind("ArrowRight",
        (state, dispatch, view) => arrowHandler(state, dispatch, view, "right"));
    bind("ArrowUp",
        (state, dispatch, view) => arrowHandler(state, dispatch, view, "up"));
    bind("ArrowDown",
        (state, dispatch, view) => arrowHandler(state, dispatch, view, "down"));

    bind("Alt-ArrowUp", joinUp);
    bind("Alt-ArrowDown", joinDown);
    bind("Mod-BracketLeft", lift);

    bind("Escape", selectParentNode);

    bind("Mod-b", toggleMark(state.schema.marks.strong));
    bind("Mod-B", toggleMark(state.schema.marks.strong));

    bind("Mod-i", toggleMark(state.schema.marks.em));
    bind("Mod-I", toggleMark(state.schema.marks.em));

    bind("Shift-Ctrl-8",
        toggleList(state.schema.nodes.bullet_list, state.schema.nodes.list_item));
    bind("Shift-Ctrl-9", toggleList(state.schema.nodes.ordered_list,
        state.schema.nodes.list_item));

    bind("Ctrl->", wrapIn(state.schema.nodes.blockquote));

    const breakCommands = chainCommands(exitCode, (state, dispatch) => {
        dispatch(state.tr.replaceSelectionWith(
            state.schema.nodes.hard_break.create()).scrollIntoView());
        return true;
    });

    bind("Mod-Enter", breakCommands);
    bind("Shift-Enter", breakCommands);
    bind("Ctrl-Enter", breakCommands);

    bind("Enter", splitListItem(state.schema.nodes.list_item));

    bind("Shift-Ctrl-0", setBlockType(state.schema.nodes.paragraph));

    for (let i = 1; i <= 6; i++) {
        bind("Shift-Ctrl-" + i,
            setBlockType(state.schema.nodes.heading, {level: i}));
    }

    bind("Mod-_", (state, dispatch) => {
        dispatch(state.tr.replaceSelectionWith(
            state.schema.nodes.horizontal_rule.create()).scrollIntoView());
        return true;
    });

    bind("Tab", sinkListItem(state.schema.nodes.list_item));
    bind("Shift-Tab", liftListItem(state.schema.nodes.list_item));

    return [keys, ...nodeKeyMaps, ...markKeyMaps].map(keys => keymap(keys));
}